import React, {
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useState,
} from "react";
import {
  FiSave,
  FiPrinter,
  FiXCircle,
  FiSearch,
  FiEdit2,
  FiTrash2,
  FiLogOut,
  FiChevronsLeft,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsRight,
  FiFileText,
  FiFile,
  FiLayout,
} from "react-icons/fi";
import { IoSettingsOutline } from "react-icons/io5";
import { IoMdArrowDropdown } from "react-icons/io";
import { HiRefresh } from "react-icons/hi";

/**
 * ActionToolbar
 *
 * A fully customizable toolbar that is the visual replacement for SecondaryNav.
 * Supports all the same props: visibility toggles, disabled states, permissions,
 * event handlers, custom buttons, navigation controls, and forwarded refs.
 *
 * Icons can be passed as:
 *   - A React component/element (e.g. a react-icons icon)  →  rendered directly
 *   - A string (image URL / imported image path)            →  rendered as <img>
 * Default icons use react-icons/fi (Feather Icons).
 */

// ─── helpers ────────────────────────────────────────────────────────────────

/** Renders either an <img> src string or a React icon element / component. */
/** Renders either an <img> src string or a React icon element / component. */
const IconRenderer = ({ icon, size = 20, color = "#1a2e4a", alt = "" }) => {
  if (!icon) return null;
  if (typeof icon === "string") {
    return (
      <img
        src={icon}
        alt={alt}
        style={{ objectFit: "contain", width: size, height: size }}
      />
    );
  }
  if (React.isValidElement(icon)) return icon;
  const IconComponent = icon;
  return <IconComponent size={size} color={color} strokeWidth={1.8} />;
};

// ─── toolbar button ──────────────────────────────────────────────────────────

const ToolbarButton = ({
  icon,
  text,
  onClick,
  disabled = false,
  show = true,
  className = "",
  buttonRef = null,
  iconSize = 20,
  iconColor = "#1a2e4a",
  buttonBgColor = "",
  buttonTextColor = "",
  buttonWidth = "",
  buttonHeight = "",
  size = "medium",
  style = {},
  id,
}) => {
  if (!show) return null;

  const customStyle = {
    ...style,
    ...(buttonWidth ? { width: buttonWidth, minWidth: buttonWidth } : {}),
    ...(buttonHeight ? { height: buttonHeight } : {}),
    ...(buttonBgColor ? { backgroundColor: buttonBgColor } : {}),
    ...(buttonTextColor ? { color: buttonTextColor } : {}),
  };

  const isCustomSizeString =
    typeof size === "string" && (size.includes("w-") || size.includes("h-"));

  let sizeClasses = "";
  if (isCustomSizeString) {
    sizeClasses = size;
  } else if (size === "small") {
    sizeClasses = "px-1.5 py-1 sm:px-2.5 sm:py-1 min-w-[44px] sm:min-w-[54px]";
  } else if (size === "large") {
    sizeClasses = "px-3 py-2 sm:px-6 sm:py-2.5 min-w-[60px] sm:min-w-[80px]";
  } else {
    sizeClasses = "px-2 py-1.5 sm:px-5 sm:py-2 min-w-[52px] sm:min-w-[70px]";
  }
 
  const textSizeClasses =
    size === "small"
      ? "text-[10px] sm:text-[11px]"
      : size === "large"
        ? "text-[11px] sm:text-[13px]"
        : "text-[10px] sm:text-[12px]";

  return (
    <button
      id={id}
      ref={buttonRef}
      className={`
        toolbar-btn
        rounded-md
        flex
        bg-white
        flex-col
        items-center
        justify-center
        gap-0.5
        sm:gap-1
        ${sizeClasses}
        transition-all
        focus:outline-none
        focus:ring-2
        focus:ring-[#1a3abf]
        focus:border-[#1a3abf]
        text-[#1a2e4a]
        ${disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-50 hover:border-[#1a3abf] cursor-pointer"}
        ${className}
      `}
      onClick={onClick}
      disabled={disabled}
      style={customStyle}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !disabled) onClick();
      }}
    >
      <IconRenderer icon={icon} size={iconSize} color={iconColor} alt={text} />
      <span
        className={`${textSizeClasses} leading-none text-inherit font-sans`}
      >
        {text}
      </span>
    </button>
  );
};

// ─── nav arrow button ────────────────────────────────────────────────────────

const NavArrowButton = ({
  icon,
  onClick,
  iconSize = 18,
  iconColor = "#1a2e4a",
  buttonBgColor = "",
  buttonClassName = "",
  buttonStyle = {},
  buttonWidth = "",
  buttonHeight = "",
  size = "medium",
  title = "",
}) => {
  const customStyle = {
    ...buttonStyle,
    ...(buttonWidth ? { width: buttonWidth, minWidth: buttonWidth } : {}),
    ...(buttonHeight ? { height: buttonHeight } : {}),
    ...(buttonBgColor ? { backgroundColor: buttonBgColor } : {}),
  };

  const isCustomSizeString =
    typeof size === "string" && (size.includes("w-") || size.includes("h-"));

  let arrowSizeClasses = "";
  if (isCustomSizeString) {
    arrowSizeClasses = size;
  } else if (size === "small") {
    arrowSizeClasses = "w-[24px] h-[24px] sm:w-[30px] sm:h-[30px]";
  } else if (size === "large") {
    arrowSizeClasses = "w-[34px] h-[34px] sm:w-[44px] sm:h-[44px]";
  } else {
    arrowSizeClasses = "w-[30px] h-[30px] sm:w-[40px] sm:h-[40px]";
  }

  return (
    <button
      title={title}
      style={customStyle}
      className={`
        toolbar-btn
        rounded-md
        bg-white
        flex
        items-center
        justify-center
        ${arrowSizeClasses}
        shrink-0
        hover:bg-blue-50
        hover:border-[#1a3abf]
        transition-all
        cursor-pointer
        focus:outline-none
        focus:ring-2
        focus:ring-[#1a3abf]
        focus:border-[#1a3abf]
        ${buttonClassName}
      `}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter") onClick();
      }}
    >
      <IconRenderer icon={icon} size={iconSize} color={iconColor} alt={title} />
    </button>
  );
};

// ─── main component ──────────────────────────────────────────────────────────

const ActionToolbar = forwardRef(
  (
    {
      // ── Custom Button IDs ──────────────────────────────────────────────────
      saveBtnId,
      printBtnId,
      clearBtnId,
      findBtnId,
      editBtnId,
      deleteBtnId,
      exitBtnId,

      // ── Styling props ──────────────────────────────────────────────────────
      width = "100%",
      height = "auto",
      bgColor = "#EAF0FC",
      buttonGap = "4px",
      arrowGap = "4px",
      className = "",
      iconSize = 20,
      iconColor = "#1a2e4a",
      buttonClassName = "",
      buttonStyle = {},
      buttonBgColor = "",
      buttonTextColor = "",
      buttonWidth = "",
      buttonHeight = "",
      navBgColor = "",
      navIconColor = "",
      navBtnClassName = "",
      size = "medium", // "small" | "medium" | "large" | custom Tailwind string e.g. "w-[40px] h-[40px]"

      // ── Button visibility props ────────────────────────────────────────────
      showSave = true,
      showPrint = true,
      showClear = true,
      showFind = true,
      showEdit = true,
      showDelete = true,
      showExit = true,
      showNavigation = true,
      showPdf = false,
      showExcel = false,
      showDesign = false,
      designActive = false,

      // ── Button state props ─────────────────────────────────────────────────
      saveDisabled = false,
      editDisabled = false,
      deleteDisabled = false,
      findDisabled = false,
      dataFound = false,

      // ── Permission props ───────────────────────────────────────────────────
      permissions = {},
      permissionKey = "", // e.g. "SchemeManagement"

      // ── Event handlers ─────────────────────────────────────────────────────
      onSave = () => {},
      onPrint = () => {},
      onClear = () => {},
      onFind = () => {},
      onEdit = () => {},
      onDelete = () => {},
      onExit = () => {},
      onFirst = () => {},
      onPrevious = () => {},
      onNext = () => {},
      onLast = () => {},
      onPdf = () => {},
      onExcel = () => {},
      onDesign = () => {},

      // ── Custom icon overrides ──────────────────────────────────────────────
      // Each can be a react-icons component, a React element, or an image src string
      saveIcon = FiSave,
      printIcon = FiPrinter,
      clearIcon = FiXCircle,
      findIcon = FiSearch,
      editIcon = FiEdit2,
      deleteIcon = FiTrash2,
      exitIcon = FiLogOut,
      firstIcon = FiChevronsLeft,
      previousIcon = FiChevronLeft,
      nextIcon = FiChevronRight,
      lastIcon = FiChevronsRight,
      pdfIcon = FiFileText,
      excelIcon = FiFile,
      designIcon = FiLayout,

      // ── Custom buttons ─────────────────────────────────────────────────────
      // Shape: { icon, text, onClick, disabled, show, className, style }
      customButtons = [],

      // ── Ref callback (mirrors SecondaryNav's onRefsUpdate) ─────────────────
      onRefsUpdate = null,

      // ── Settings Bar Props ─────────────────────────────────────────────────
      showSettingsBar = false,
      showSettingsDropdown = true,
      showRefresh = true,
      isRefreshing = false,
      onRefresh = () => {},
      settingsOptions = [],
      settingsBarHeight = "24px",
      settingsBarBgColor = "#EAF0FC",
      settingsBarClassName = "",
      settingsBarStyle = {},
    },
    ref,
  ) => {
    const saveButtonRef = useRef(null);
    const editButtonRef = useRef(null);
    const clearButtonRef = useRef(null);
    const findButtonRef = useRef(null);
    const deleteButtonRef = useRef(null);
    const exitButtonRef = useRef(null);
    const [showStngDropdown, setShowStngDropdown] = useState(false);

    // Expose refs to parent
    useImperativeHandle(ref, () => ({
      saveButtonRef,
      editButtonRef,
      clearButtonRef,
      findButtonRef,
      deleteButtonRef,
      exitButtonRef,
    }));

    // Notify parent when refs or key state changes
    useEffect(() => {
      if (onRefsUpdate) {
        onRefsUpdate({ saveButtonRef, editButtonRef, clearButtonRef, findButtonRef, deleteButtonRef, exitButtonRef });
      }
    }, [onRefsUpdate, saveDisabled, editDisabled, dataFound]);

    // Permission helper (same logic as SecondaryNav)
    const hasPermission = (action) => {
      if (!permissionKey || !permissions[permissionKey]) return true;
      return permissions[permissionKey]?.actions?.[action] || false;
    };

    return (
      <>
        <style>{`
          .toolbar {
            border-top: 1px solid #b8cce0;
            border-bottom: 1px solid #b8cce0;
          }
          .toolbar-btn {
            border: 1px solid #b8cce0;
          }
        `}</style>

        <div className="w-full flex flex-col relative">
          {/* Settings Bar */}
          {showSettingsBar && (
            <div
              className={`w-full flex-shrink-0 border-b border-gray-200 flex items-center gap-2 px-2 box-border text-[11px] text-gray-600 select-none relative ${settingsBarClassName}`}
              style={{
                height: settingsBarHeight,
                backgroundColor: settingsBarBgColor,
                ...settingsBarStyle,
              }}
            >
              {showSettingsDropdown && settingsOptions.length > 0 && (
                <div className="relative flex items-center h-full">
                  <button
                    onClick={() => setShowStngDropdown((prev) => !prev)}
                    className="flex items-center justify-center gap-1 border-none bg-transparent h-full w-auto text-gray-600 hover:text-gray-800 transition-colors cursor-pointer"
                    type="button"
                  >
                    <IoSettingsOutline className="w-[14px] h-[14px]" />
                    <IoMdArrowDropdown className="w-[11px] h-[11px]" />
                  </button>

                  {showStngDropdown && (
                    <div
                      className="absolute left-0 top-6 bg-white border border-gray-200 rounded shadow-lg py-1 z-50 flex flex-col font-Inter"
                      style={{ width: "160px" }}
                      onMouseLeave={() => setShowStngDropdown(false)}
                    >
                      {settingsOptions.map((opt, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => {
                            setShowStngDropdown(false);
                            opt.onClick();
                          }}
                          className="w-full text-left px-3 py-1.5 bg-transparent border-none hover:bg-gray-100 text-gray-700 text-xs cursor-pointer border-t border-gray-100 first:border-none"
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {showRefresh && (
                <button
                  onClick={onRefresh}
                  disabled={isRefreshing}
                  className="flex items-center gap-1 border-none bg-transparent text-[#1a3abf] hover:text-blue-800 font-semibold focus:outline-none focus:ring-0 focus:ring-offset-0 disabled:opacity-50 cursor-pointer"
                  type="button"
                >
                  <HiRefresh
                    className={`w-3 h-3 text-[#1a3abf] ${isRefreshing ? "animate-spin" : "transition-transform duration-500 hover:rotate-180"}`}
                  />
                  <span>{isRefreshing ? "Refreshing..." : "Refresh"}</span>
                </button>
              )}
            </div>
          )}

          <div
            className={`box-border toolbar ${className}`}
            style={{ width, height, backgroundColor: bgColor }}
          >
          <div
            className="flex items-center flex-wrap rounded-[10px] px-2.5 py-1.5"
            style={{ gap: buttonGap }}
          >
            {/* Save */}
            {showSave && hasPermission("Save") && (
              <ToolbarButton
                id={saveBtnId}
                icon={saveIcon}
                text="Save"
                onClick={onSave}
                disabled={saveDisabled || dataFound}
                show={showSave && hasPermission("Save")}
                className={`${dataFound ? "disabled" : ""} ${buttonClassName}`}
                style={buttonStyle}
                buttonBgColor={buttonBgColor}
                buttonTextColor={buttonTextColor}
                buttonWidth={buttonWidth}
                buttonHeight={buttonHeight}
                buttonRef={saveButtonRef}
                iconSize={iconSize}
                iconColor={iconColor}
                size={size}
              />
            )}

            {/* Print */}
            {showPrint && (
              <ToolbarButton
                id={printBtnId}
                icon={printIcon}
                text="Print"
                onClick={onPrint}
                show={showPrint}
                className={buttonClassName}
                style={buttonStyle}
                buttonBgColor={buttonBgColor}
                buttonTextColor={buttonTextColor}
                buttonWidth={buttonWidth}
                buttonHeight={buttonHeight}
                iconSize={iconSize}
                iconColor={iconColor}
                size={size}
              />
            )}

            {/* Clear */}
            {showClear && (
              <ToolbarButton
                id={clearBtnId}
                icon={clearIcon}
                text="Clear"
                onClick={onClear}
                show={showClear}
                className={buttonClassName}
                style={buttonStyle}
                buttonBgColor={buttonBgColor}
                buttonTextColor={buttonTextColor}
                buttonWidth={buttonWidth}
                buttonHeight={buttonHeight}
                buttonRef={clearButtonRef}
                iconSize={iconSize}
                iconColor={iconColor}
                size={size}
              />
            )}

            {/* Find */}
            {showFind && hasPermission("Find") && (
              <ToolbarButton
                id={findBtnId}
                icon={findIcon}
                text="Find"
                onClick={onFind}
                disabled={findDisabled}
                show={showFind && hasPermission("Find")}
                className={buttonClassName}
                style={buttonStyle}
                buttonBgColor={buttonBgColor}
                buttonTextColor={buttonTextColor}
                buttonWidth={buttonWidth}
                buttonHeight={buttonHeight}
                buttonRef={findButtonRef}
                iconSize={iconSize}
                iconColor={iconColor}
                size={size}
              />
            )}

            {/* Edit */}
            {showEdit && hasPermission("Edit") && (
              <ToolbarButton
                id={editBtnId}
                icon={editIcon}
                text="Edit"
                onClick={onEdit}
                disabled={editDisabled || !dataFound}
                show={showEdit && hasPermission("Edit")}
                className={`${dataFound ? "" : "disabled"} ${buttonClassName}`}
                style={buttonStyle}
                buttonBgColor={buttonBgColor}
                buttonTextColor={buttonTextColor}
                buttonWidth={buttonWidth}
                buttonHeight={buttonHeight}
                buttonRef={editButtonRef}
                iconSize={iconSize}
                iconColor={iconColor}
                size={size}
              />
            )}

            {/* Delete */}
            {showDelete && hasPermission("Delete") && (
              <ToolbarButton
                id={deleteBtnId}
                icon={deleteIcon}
                text="Delete"
                onClick={onDelete}
                disabled={deleteDisabled || !dataFound}
                show={showDelete && hasPermission("Delete")}
                className={`${dataFound ? "" : "disabled"} ${buttonClassName}`}
                style={buttonStyle}
                buttonBgColor={buttonBgColor}
                buttonTextColor={buttonTextColor}
                buttonWidth={buttonWidth}
                buttonHeight={buttonHeight}
                buttonRef={deleteButtonRef}
                iconSize={iconSize}
                iconColor={iconColor}
                size={size}
              />
            )}

            {/* Pdf */}
            {showPdf && (
              <ToolbarButton
                icon={pdfIcon}
                text="To Pdf"
                onClick={onPdf}
                show={showPdf}
                className={buttonClassName}
                style={buttonStyle}
                buttonBgColor={buttonBgColor}
                buttonTextColor={buttonTextColor}
                buttonWidth={buttonWidth}
                buttonHeight={buttonHeight}
                iconSize={iconSize}
                iconColor={iconColor}
                size={size}
              />
            )}

            {/* Excel */}
            {showExcel && (
              <ToolbarButton
                icon={excelIcon}
                text="To Excel"
                onClick={onExcel}
                show={showExcel}
                className={buttonClassName}
                style={buttonStyle}
                buttonBgColor={buttonBgColor}
                buttonTextColor={buttonTextColor}
                buttonWidth={buttonWidth}
                buttonHeight={buttonHeight}
                iconSize={iconSize}
                iconColor={iconColor}
                size={size}
              />
            )}

            {/* Design */}
            {showDesign && (
              <ToolbarButton
                icon={designIcon}
                text="Design"
                onClick={onDesign}
                show={showDesign}
                style={buttonStyle}
                buttonBgColor={buttonBgColor}
                buttonTextColor={buttonTextColor}
                buttonWidth={buttonWidth}
                buttonHeight={buttonHeight}
                iconSize={iconSize}
                iconColor={designActive ? "#2C4ABB" : iconColor}
                size={size}
                className={
                  designActive
                    ? `!border-[#2C4ABB] !bg-[#E8EEF8] !text-[#2C4ABB] ${buttonClassName}`
                    : buttonClassName
                }
              />
            )}

            {/* Custom Buttons */}
            {customButtons.map((btn, index) => (
              <ToolbarButton
                key={index}
                icon={btn.icon}
                text={btn.text}
                onClick={btn.onClick}
                disabled={btn.disabled}
                show={btn.show !== false}
                className={`${btn.className || ""} ${buttonClassName}`}
                style={{ ...buttonStyle, ...btn.style }}
                buttonBgColor={btn.buttonBgColor || buttonBgColor}
                buttonTextColor={btn.buttonTextColor || buttonTextColor}
                buttonWidth={btn.buttonWidth || buttonWidth}
                buttonHeight={btn.buttonHeight || buttonHeight}
                iconSize={btn.iconSize ?? iconSize}
                iconColor={btn.iconColor ?? iconColor}
                size={btn.size ?? size}
              />
            ))}

            {/* Exit */}
            {showExit && (
              <ToolbarButton
                id={exitBtnId}
                icon={exitIcon}
                text="Exit"
                onClick={onExit}
                show={showExit}
                className={buttonClassName}
                style={buttonStyle}
                buttonBgColor={buttonBgColor}
                buttonTextColor={buttonTextColor}
                buttonWidth={buttonWidth}
                buttonHeight={buttonHeight}
                buttonRef={exitButtonRef}
                iconSize={iconSize}
                iconColor={iconColor}
                size={size}
              />
            )}

            {/* Navigation Controls */}
            {showNavigation && (
              <div
                className="ml-auto flex flex-row items-center"
                style={{ gap: arrowGap }}
              >
                <NavArrowButton
                  icon={firstIcon}
                  onClick={onFirst}
                  title="First"
                  buttonClassName={`${buttonClassName} ${navBtnClassName}`}
                  buttonStyle={buttonStyle}
                  buttonBgColor={navBgColor || buttonBgColor}
                  buttonWidth={buttonWidth}
                  buttonHeight={buttonHeight}
                  iconSize={iconSize}
                  iconColor={navIconColor || iconColor}
                  size={size}
                />
                <NavArrowButton
                  icon={previousIcon}
                  onClick={onPrevious}
                  title="Previous"
                  buttonClassName={`${buttonClassName} ${navBtnClassName}`}
                  buttonStyle={buttonStyle}
                  buttonBgColor={navBgColor || buttonBgColor}
                  buttonWidth={buttonWidth}
                  buttonHeight={buttonHeight}
                  iconSize={iconSize}
                  iconColor={navIconColor || iconColor}
                  size={size}
                />
                <NavArrowButton
                  icon={nextIcon}
                  onClick={onNext}
                  title="Next"
                  buttonClassName={`${buttonClassName} ${navBtnClassName}`}
                  buttonStyle={buttonStyle}
                  buttonBgColor={navBgColor || buttonBgColor}
                  buttonWidth={buttonWidth}
                  buttonHeight={buttonHeight}
                  iconSize={iconSize}
                  iconColor={navIconColor || iconColor}
                  size={size}
                />
                <NavArrowButton
                  icon={lastIcon}
                  onClick={onLast}
                  title="Last"
                  buttonClassName={`${buttonClassName} ${navBtnClassName}`}
                  buttonStyle={buttonStyle}
                  buttonBgColor={navBgColor || buttonBgColor}
                  buttonWidth={buttonWidth}
                  buttonHeight={buttonHeight}
                  iconSize={iconSize}
                  iconColor={navIconColor || iconColor}
                  size={size}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
    );
  },
);

ActionToolbar.displayName = "ActionToolbar";

export default ActionToolbar;
